<template>
	<div
		class="c-Textarea"
		:class="textareaClassObj"
	>
		<textarea
			ref="textarea"
			v-model="text"
			:aria-label="label"
			:disabled="disabled"
			:name="name"
			:placeholder="placeholder"
			:readonly="readOnly"
			:required="required"
			class="c-Textarea__input"
			@input="handleInput"
			@change="handleChange"
		/>
		<span
			class="c-Textarea__label"
			@mousedown="labelClick($event)"
			@keydown="labelClick($event)"
		>
			{{ label }}
		</span>
	</div>
</template>
<script>
export default {
	name: 'Textarea',
	model: {
		prop: 'data',
	},
	props: {
		/**
     * The Textarea's label
     */
		label: {
      type: String,
      required: false,
      default: '',
		},
		/**
     * The Textarea's data variable
     */
		data: {
			type: String,
			required: false,
			default: '',
		},
		/**
     * If the Textarea is disabled (html attribute disabled)
     * @values true, false
     */
		disabled: {
			type: Boolean,
			required: false,
			default: false,
		},
		/**
     * The Textarea name (html attribute name)
     */
		name: {
			type: String,
			required: false,
			default: '',
		},
		/**
     * The Textarea's placeholder text
     */
		placeholder: {
			type: String,
			required: false,
			default: '',
		},
		/**
     * If the Textarea is read-only (html attribute readonly)
     * @values true, false
     */
		readOnly: {
			type: Boolean,
			required: false,
			default: false,
		},
		/**
     * If the Textarea is read-only (html attribute readonly)
     * @values true, false
     */
		isActive: {
			type: Boolean,
			required: false,
			default: false,
		},
		/**
		 * Textarea's resize direction
		 * @values default, vertical, horizontal, none
		 */
		resize: {
			type: String,
			required: false,
			default: 'default',
			validator (resize) {
				return [
					'default',
					'vertical',
					'horizontal',
					'none',
				].includes(resize)
			}
		},
		/**
     * If the Textarea is required (html attribute required)
     * @values true, false
     */
    required: {
      type: Boolean,
      required: false,
      default: () => false,
		},
	},
	data() {
		return {
			text: '',
		};
	},
	computed: {
		textareaClassObj() {
			return {
				'is-active': this.isActive,
				'has-value': this.text !== '' || this.placeholder !== '',
				'has-vertical-resize': this.resize === 'vertical',
				'has-horizontal-resize': this.resize === 'horizontal',
				'has-no-resize': this.resize === 'none',
				'has-label': this.label,
			};
		},
	},
	watch: {
		data: {
			handler(newVal) {
        const val = newVal !== undefined && newVal !== null ? `${newVal}` : '';
        this.text = val;
      },
      immediate: true,
		},
	},
	methods: {
		labelClick(event) {
			event.preventDefault()
			this.$refs.textarea.focus()
		},
		handleInput() {
			/**
			 * Triggers when the Textarea changes it's value during value input (@input)
			 * @event input
			 * @property {string} value new value set
			 */
			this.$emit('input', this.text);
		},
		handleChange() {
			/**
			 * Triggers when the Textarea changes it's value (@change)
			 * @event change
			 * @property {string} value new value set
			 */
			this.$emit('change', this.text);
		}
	}
}
</script>
<style lang="scss" scoped>
@import '../../scss/variables.scss';

.c-Textarea {
	$font-size-label: 16px;
  $font-size-label-top: 12px;
  $font-size-value: 16px;
	position: relative;
	width: 100%;
	height: 100%;

	&__label {
    display: block;
    font-family: $primary-font;
    font-size: $font-size-label;
		color: $un-n700;
		cursor: pointer;
    position: absolute;
    top: 15px;
    left: calc(#{$base-padding / 2});
    transition: all $default-time $default-easing;
		z-index: 2;
	}

  &__input {
		overflow: auto;
		min-height: 90px; // The exact height of 3 lines of text
		min-width: 240px;
		width: 100%;
		height: 100%;
    outline: none;
		border: 1px solid $un-gray-light;
		border-radius: $border-radius;
		background-color: $un-white;
    font-family: $primary-font;
    font-size: $font-size-value;
    line-height: 24px;
    color: $un-n700;
		padding: 8px;
    box-sizing: border-box;
    cursor: pointer;
		position: relative;
		transition:
			border $default-time $default-easing,
			box-shadow $default-time $default-easing,
			;
		z-index: 1;
		
		&:invalid {
			border: 1px solid $un-red;
			box-shadow: inset 0 0 0 1px $un-red;

			+.c-Textarea__label {
				color: $un-red;
			}
		}

		&:disabled {
			border-color: $un-n500;
			background-color: $un-n100;
      cursor: not-allowed;
			resize: none;

			+.c-Textarea__label {
				cursor: not-allowed;
				color: $un-n500;
			}
		}

		&:focus {
			border: 1px solid $un-blue;
			box-shadow: inset 0 0 0 1px $un-blue;

			+.c-Textarea__label {
				color: $un-blue;
				top: calc(#{$base-padding} / 2) !important;
				font-size: $font-size-label-top !important;
			}
		}

		&:read-only {
			border: 1px solid $un-gray-light;
			box-shadow: none;

			+.c-Textarea__label {
				color: $un-n700;
			}
		}
	}

	&.is-active {
		.c-Textarea__input {
			border: 1px solid $un-blue;
			box-shadow: inset 0 0 0 1px $un-blue;
		}

    .c-Textarea__label {
      color: $un-blue;
    }
  }

	&.has-label {
		.c-Textarea__input {
			padding:
				22px
				calc(#{$base-padding / 2})
				calc(#{$base-padding / 2})
				calc(#{$base-padding / 2});
			min-height: 104px;
    }
	}

	&.has-value {
		.c-Textarea__label {
      top: calc(#{$base-padding} / 2) !important;
      font-size: $font-size-label-top !important;
    }
	}
	
	&.has-vertical-resize {
		.c-Textarea__input {
			resize: vertical;
		}
	}

	&.has-horizontal-resize {
		.c-Textarea__input {
			resize: horizontal;
		}
	}

	&.has-no-resize {
		.c-Textarea__input {
			resize: none;
		}
	}
}
</style>
